/**
 * RequireAuth — route-level middleware guarding enrollment and learner-only routes.
 *
 * Blocks rendering of any protected page for unauthenticated visitors and
 * redirects them to /auth with a `redirect` param so they return after sign-in.
 * Re-validates the session with the auth server via AuthGate to prevent stale
 * client flags from leaking authed UI onto public routes.
 *
 * When a redirect is needed, we show a brief "Redirecting to sign in…" state
 * before navigating so the user sees an intentional transition rather than a
 * flash of protected UI or a blank frame.
 */

import { ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { useAuthGateRecovery } from "@/hooks/useAuthGateRecovery";
import { AuthAccessStatus } from "./AuthAccessStatus";
import { useAgeVerificationGate } from "@/hooks/useAgeVerificationGate";
import { AGE_GATE_PATH, ageGateUrl } from "@/lib/ageGate";

interface RequireAuthProps {
  children: ReactNode;
}

const REDIRECT_DELAY_MS = 250;

export function RequireAuth({ children }: RequireAuthProps) {
  const { user, isAuthenticated, isLoading, initializationFailed } = useAuth();
  const userId = user?.id ?? null;
  const { ready: ageReady, promptRequired, lookupFailed } = useAgeVerificationGate();
  const location = useLocation();
  const navigate = useNavigate();
  const [verification, setVerification] = useState<{ userId: string | null; verified: boolean | null; failed: boolean }>({ userId: null, verified: null, failed: false });
  const verified = verification.userId === userId ? verification.verified : null;
  const pending = isLoading || verified === null || (isAuthenticated && !ageReady);
  const failed = useAuthGateRecovery(pending, initializationFailed || verification.failed || (isAuthenticated && lookupFailed), isLoading ? "auth" : verified === null ? "session" : "age");
  const [showChecking, setShowChecking] = useState(false);

  // Only reveal the "checking" state if the session check is genuinely slow.
  useEffect(() => {
    const t = window.setTimeout(() => setShowChecking(true), 300);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setVerification({ userId, verified: null, failed: false });
    if (isLoading) return;
    if (!isAuthenticated) {
      setVerification({ userId, verified: false, failed: false });
      return;
    }
    supabase.auth.getUser().then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        setVerification({ userId, verified: null, failed: true });
        return;
      }
      const realUser = !!data.user && data.user.id === userId && !(data.user as User & { is_anonymous?: boolean }).is_anonymous;
      setVerification({ userId, verified: realUser, failed: false });
    }).catch(() => {
      if (!cancelled) setVerification({ userId, verified: null, failed: true });
    });
    return () => { cancelled = true; };
  }, [isAuthenticated, isLoading, userId]);

  const mustRedirect =
    !isLoading && verified !== null && (!isAuthenticated || !verified);

  useEffect(() => {
    if (failed || isLoading || !ageReady || !isAuthenticated || !promptRequired) return;
    if (location.pathname === AGE_GATE_PATH) return;
    navigate(ageGateUrl(location.pathname + location.search), { replace: true });
  }, [failed, isLoading, ageReady, isAuthenticated, promptRequired, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (failed || !mustRedirect) return;
    const redirect = encodeURIComponent(location.pathname + location.search);
    const t = window.setTimeout(() => {
      navigate(`/auth?redirect=${redirect}`, { replace: true });
    }, REDIRECT_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [failed, mustRedirect, location.pathname, location.search, navigate]);

  if (failed) return <AuthAccessStatus failed mainTarget={!location.pathname.startsWith("/app/")} signInHref={isLoading ? `/auth?redirect=${encodeURIComponent(location.pathname + location.search)}` : undefined} />;

  if (isAuthenticated && promptRequired && location.pathname !== AGE_GATE_PATH) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background text-foreground"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">Redirecting…</p>
      </div>
    );
  }

  if (mustRedirect) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background text-foreground"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">Redirecting to sign in…</p>
      </div>
    );
  }

  if (pending) {
    // Suppress the spinner for fast session checks: showing it immediately
    // causes a visible flash + layout jump right after a deep-link redirect.
    if (!showChecking) return <div className="min-h-screen bg-background" aria-hidden />;
    return (
      <div
        role="status"
        aria-live="polite"
        className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background text-foreground"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">Checking your session…</p>
      </div>
    );
  }

  return <>{children}</>;
}
